import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Pin, ClipboardList, Calendar, Sparkles, Home } from "lucide-react";

const CATEGORIES = [
  { v: "points_generaux", label: "Points généraux", icon: ClipboardList, tint: "border-primary/40 bg-primary/5" },
  { v: "sur_evenement", label: "Sur événement", icon: Sparkles, tint: "border-fuchsia-400/40 bg-fuchsia-500/5" },
  { v: "conges_recuperations", label: "Congés / Récupérations", icon: Calendar, tint: "border-emerald-400/40 bg-emerald-500/5" },
  { v: "menage", label: "Ménage", icon: Home, tint: "border-amber-400/40 bg-amber-500/5" },
];

const EMPTY_NOTE = { title: "", content: "", items: [], category: "points_generaux" };

export default function ServiceNotesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [notes, setNotes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_NOTE);
  const [newItem, setNewItem] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/service-notes"); setNotes(data); }
    catch (e) { toast.error(extractError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = (category) => { setEditingId(null); setForm({ ...EMPTY_NOTE, category: category || "points_generaux" }); setNewItem(""); setDialogOpen(true); };
  const openEdit = (n) => { setEditingId(n.id); setForm({ title: n.title || "", content: n.content || "", items: n.items || [], category: n.category }); setNewItem(""); setDialogOpen(true); };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Titre requis"); return; }
    try {
      if (editingId) await api.patch(`/service-notes/${editingId}`, form);
      else await api.post("/service-notes", form);
      toast.success(editingId ? "Note mise à jour · admins notifiés" : "Note ajoutée · admins notifiés");
      setDialogOpen(false); load();
    } catch (e) { toast.error(extractError(e)); }
  };
  const remove = async (n) => {
    if (!confirm(`Supprimer la note « ${n.title} » ?`)) return;
    try { await api.delete(`/service-notes/${n.id}`); toast.success("Supprimée"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };
  const toggleItem = async (n, idx) => {
    if (!isAdmin) return;
    const items = [...(n.items || [])];
    items[idx] = { ...items[idx], checked: !items[idx].checked };
    try { await api.patch(`/service-notes/${n.id}`, { items }); load(); }
    catch (e) { toast.error(extractError(e)); }
  };
  const addItem = () => { if (!newItem.trim()) return; setForm({ ...form, items: [...(form.items || []), { text: newItem.trim(), checked: false }] }); setNewItem(""); };
  const removeItem = (idx) => { setForm({ ...form, items: form.items.filter((_, i) => i !== idx) }); };

  const byCat = CATEGORIES.reduce((acc, c) => { acc[c.v] = notes.filter(n => n.category === c.v); return acc; }, {});

  return (
    <div className="space-y-6" data-testid="service-notes-page">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-brand text-4xl sm:text-5xl">Notes de service</h1>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mt-1">Consignes internes de l'agence</div>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            {notes.length === 0 && (
              <Button variant="outline" className="rounded-full" onClick={async () => {
                if (!confirm("Importer les 11 notes de service AAE par défaut ?")) return;
                try { const { data } = await api.post("/service-notes/seed-defaults"); toast.success(`${data.created} note(s) importée(s)`); load(); }
                catch (e) { toast.error(extractError(e)); }
              }} data-testid="seed-service-notes-btn">
                <Plus className="h-4 w-4 mr-2" /> Importer les notes AAE
              </Button>
            )}
            <Button className="rounded-full bg-secondary text-secondary-foreground shadow-glow" onClick={() => openNew()} data-testid="add-service-note-btn">
              <Plus className="h-4 w-4 mr-2" /> Nouvelle note
            </Button>
          </div>
        )}
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {CATEGORIES.map(c => {
          const Icon = c.icon;
          return (
            <Card key={c.v} className={`border-2 ${c.tint} shadow-warm`} data-testid={`sn-cat-${c.v}`}>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center gap-2">
                  <Icon className="h-5 w-5 text-primary" />
                  <div className="font-brand text-xl">{c.label}</div>
                  <Badge variant="outline" className="rounded-full ml-auto">{byCat[c.v].length}</Badge>
                  {isAdmin && (
                    <Button size="icon" variant="ghost" className="rounded-full h-7 w-7" onClick={() => openNew(c.v)} title="Ajouter à cette catégorie">
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
                {byCat[c.v].length === 0 ? (
                  <div className="text-sm italic text-muted-foreground py-3 text-center">Aucune note dans cette catégorie</div>
                ) : (
                  <div className="space-y-2">
                    {byCat[c.v].map(n => (
                      <div key={n.id} className="rounded-lg border border-border/60 bg-card p-3 space-y-2" data-testid={`sn-${n.id}`}>
                        <div className="flex items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-sm">{n.title}</div>
                            {n.content && <div className="text-xs text-muted-foreground whitespace-pre-line mt-1">{n.content}</div>}
                          </div>
                          {isAdmin && (
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(n)} data-testid={`sn-edit-${n.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => remove(n)}><Trash2 className="h-3.5 w-3.5" /></Button>
                            </div>
                          )}
                        </div>
                        {n.items?.length > 0 && (
                          <div className="space-y-1 pl-1">
                            {n.items.map((it, i) => (
                              <div key={i} className="flex items-start gap-2 text-sm">
                                <Pin className="h-4 w-4 mt-0.5 shrink-0 text-primary -rotate-45 fill-primary/20" />
                                <span>{it.text}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="text-[10px] text-muted-foreground italic pt-1 border-t border-border/40">
                          {n.updated_at !== n.created_at ? "Modifiée" : "Créée"} le {(n.updated_at || n.created_at || "").slice(0, 10)} par {n.created_by_name || "?"}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>{editingId ? "Modifier la note" : "Nouvelle note de service"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Catégorie</Label>
              <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="sn-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Titre</Label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="sn-form-title" />
            </div>
            <div>
              <Label className="text-xs">Description (facultatif)</Label>
              <Textarea rows={3} value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} placeholder="Contexte / détails" />
            </div>
            <div>
              <Label className="text-xs">Points (affichés en punaises)</Label>
              <div className="space-y-1 mb-2">
                {(form.items || []).map((it, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <Pin className="h-4 w-4 text-primary -rotate-45 fill-primary/20 shrink-0" />
                    <span className="flex-1">{it.text}</span>
                    <button onClick={() => removeItem(i)} className="text-destructive"><Trash2 className="h-3 w-3" /></button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={newItem} onChange={e => setNewItem(e.target.value)} placeholder="Ajouter un point à cocher" onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addItem(); } }} />
                <Button size="sm" variant="outline" onClick={addItem}><Plus className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button className="bg-primary" onClick={save} data-testid="sn-form-save">{editingId ? "Enregistrer" : "Créer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
